<script setup lang="ts">
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import { computed } from 'vue'
import CustomDivider from '@/components/createdUi/CustomDivider.vue'

import { getRandomAvatar } from '@/utils/getRandomAvatar'

interface ISelectedItem {
  id: string | number
  name: string
  avatar?: string
  description?: string
  [key: string]: any
}

export interface ISelectedItemsGroup {
  key: string
  title: string
  items: ISelectedItem[]
  hideAvatar?: boolean
}

interface Props {
  items?: ISelectedItem[]
  groups?: ISelectedItemsGroup[]
  title?: string
  hideAvatar?: boolean
  readonly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  groups: () => [],
  hideAvatar: false,
  readonly: false,
})

const emit = defineEmits<{
  (e: 'remove', id: string | number, groupKey?: string): void
  (e: 'remove-all'): void
  (e: 'remove-group', groupKey: string): void
}>()

const visibleGroups = computed(() => props.groups.filter(group => group.items.length))
const isGrouped = computed(() => visibleGroups.value.length > 0)

const totalCount = computed(() => {
  if (isGrouped.value)
    return visibleGroups.value.reduce((sum, group) => sum + group.items.length, 0)
  return props.items.length
})

const displayTitle = computed(() => {
  if (props.title) return props.title
  return `Выбрано ${totalCount.value}:`
})

const resetAllLabel = computed(() => isGrouped.value ? 'Сбросить все' : 'Сбросить выбор')
</script>

<template>
  <VCard
    class="rounded-lg elevation-4 border bg-surface"
    :min-width="isGrouped ? 440 : 280"
    :max-width="isGrouped ? 520 : 360"
  >
    <div
      class="d-flex align-center pa-4 pb-0"
      :class="isGrouped ? 'justify-end' : 'justify-space-between ga-2'"
    >
      <div
        v-if="!isGrouped"
        class="text-body-1 font-weight-medium text-high-emphasis"
      >
        {{ displayTitle }}
      </div>
      <span
        v-if="totalCount && !readonly"
        class="text-body-2 text-medium-emphasis cursor-pointer d-inline-flex align-center flex-shrink-0"
        @click="emit('remove-all')"
      >
        {{ resetAllLabel }}
        <VIcon
          v-if="!isGrouped"
          icon="ri-close-circle-fill"
          size="18"
          class="ms-1 opacity-70"
        />
      </span>
    </div>
    <CustomDivider class="my-3" />
    <div class="overflow-y-auto pa-4 pt-0 d-flex flex-column ga-4" style="max-height: 300px;">
      <template v-if="isGrouped">
        <div
          v-for="group in visibleGroups"
          :key="group.key"
          class="d-flex flex-column ga-2"
        >
          <div class="d-flex align-center justify-space-between ga-2 ps-4">
            <div class="text-subtitle-1 font-weight-medium text-high-emphasis">
              {{ group.title }} ({{ group.items.length }})
            </div>
            <span
              v-if="!readonly"
              class="text-body-2 text-medium-emphasis cursor-pointer d-inline-flex align-center flex-shrink-0"
              @click="emit('remove-group', group.key)"
            >
              Сбросить
            </span>
          </div>
          <div
            v-for="item in group.items"
            :key="`${group.key}-${item.id}`"
            class="d-flex align-center justify-space-between py-1"
          >
            <div class="d-flex align-center ga-2 flex-grow-1 min-w-0">
              <VAvatar
                v-if="!(group.hideAvatar ?? hideAvatar)"
                variant="tonal"
                size="36"
                :image="item.avatar || getRandomAvatar()"
              />
              <div class="d-flex flex-column flex-grow-1 min-w-0">
                <span class="text-body-2 font-weight-medium text-high-emphasis text-wrap">
                  {{ item.name }}
                </span>
                <span v-if="item.description" class="text-caption text-medium-emphasis text-wrap">
                  {{ item.description }}
                </span>
              </div>
            </div>
            <VIconBtn
              v-if="!readonly"
              size="small"
              variant="text"
              color="secondary"
              class="ms-2 opacity-70 hover-opacity-100"
              @click="emit('remove', item.id, group.key)"
            >
              <VIcon icon="ri-close-circle-fill" size="18" />
            </VIconBtn>
          </div>
        </div>
      </template>
      <template v-else>
        <div
          v-for="item in items"
          :key="item.id"
          class="d-flex align-center justify-space-between py-1"
        >
          <div class="d-flex align-center ga-2 flex-grow-1 min-w-0">
            <VAvatar
              v-if="!hideAvatar"
              variant="tonal"
              size="36"
              :image="item.avatar || getRandomAvatar()"
            />
            <div class="d-flex flex-column flex-grow-1 min-w-0">
              <span class="text-body-2 font-weight-medium text-high-emphasis text-wrap">
                {{ item.name }}
              </span>
              <span v-if="item.description" class="text-caption text-medium-emphasis text-wrap">
                {{ item.description }}
              </span>
            </div>
          </div>
          <VIconBtn
            v-if="!readonly"
            size="small"
            variant="text"
            color="secondary"
            class="ms-2 opacity-70 hover-opacity-100"
            @click="emit('remove', item.id)"
          >
            <VIcon icon="ri-close-circle-fill" size="18" />
          </VIconBtn>
        </div>
      </template>
    </div>
  </VCard>
</template>
